import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CloudUpload, Copy, Search, X } from 'lucide-react';
import DynamicTable from '../../components/DynamicTable';
import Pagination from '../../components/Pagination';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';
import noBrandImage from '../../assets/img/brand/no-brand-img.png';
import emptySearchImg from '../../assets/img/brand/empty-search.png';
import { formatDateTime } from '../../utils/formateDate';
import { noImage } from './tradeInImages.js';
import TradeInImportImagesModal from './TradeInImportImagesModal.js';
import { listLibraryImages, deleteLibraryImage } from './tradeInMockApi.js';

// ERP-5289 — Trade-In Listing → "Listing Image" tab: the shared image library that Listing
// Items pick their image from. Search by name, sort by name / last modified, copy a name,
// import local files, and delete an image nothing uses any more.

const PAGE_SIZE = 20;

const usedInLabel = (r) => (r.isSystem
    ? 'Default image'
    : `${r.usedListings || 0} Listing · ${r.usedItems || 0} Items`);
// Linked to any listing item, archived ones included: deleting removes the file from storage,
// so the delete stays disabled while anything still points at it.
const isLocked = (r) => r.isSystem || Boolean(r.usedListings || r.usedItems || r.linkedItems);

// The Clipboard API rejects (asynchronously) when the page has no clipboard permission, so its
// promise is awaited and the older execCommand copy used as a fallback. True once copied.
async function copyText(text) {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch (e) {
        const area = document.createElement('textarea');
        area.value = text;
        area.setAttribute('readonly', '');
        area.style.position = 'fixed';
        area.style.opacity = '0';
        document.body.appendChild(area);
        area.select();
        let ok = false;
        try { ok = document.execCommand('copy'); } catch (err) { ok = false; }
        document.body.removeChild(area);
        return ok;
    }
}

export default function TradeInListingImageTab({ onNotify }) {
    const [rows, setRows] = useState([]);
    const [count, setCount] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('modified_at');
    const [sortOrder, setSortOrder] = useState('desc');
    const [page, setPage] = useState(1);
    const [importOpen, setImportOpen] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [copiedId, setCopiedId] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const copyTimer = useRef(null);
    const loadSeq = useRef(0);

    const [menuOpenId, setMenuOpenId] = useState(null);
    const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
    const menuRef = useRef(null);
    const buttonRefs = useRef({});

    // reloadKey forces a refetch after an import / delete; seq drops a slower, older response.
    const load = useCallback(async () => {
        const seq = ++loadSeq.current;
        const ordering = `${sortOrder === 'desc' ? '-' : ''}${sortField === 'name' ? 'name' : 'modified_at'}`;
        let data;
        try {
            data = await listLibraryImages({ search: searchTerm, ordering, page, pageSize: PAGE_SIZE });
        } catch (err) {
            console.error('Failed to load trade-in images:', err);
            data = { results: [], count: 0 };
        }
        if (seq !== loadSeq.current) return;
        setRows(data.results);
        setCount(data.count);
        setLoaded(true);
    }, [searchTerm, sortField, sortOrder, page, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);
    useEffect(() => () => clearTimeout(copyTimer.current), []);

    // Close the action menu on outside click / scroll, like the Listings tab.
    useEffect(() => {
        if (menuOpenId === null) return undefined;
        const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpenId(null); };
        const onScroll = () => setMenuOpenId(null);
        document.addEventListener('mousedown', onDown);
        window.addEventListener('scroll', onScroll, true);
        return () => {
            document.removeEventListener('mousedown', onDown);
            window.removeEventListener('scroll', onScroll, true);
        };
    }, [menuOpenId]);

    const toggleMenu = (id, e) => {
        if (e) e.stopPropagation();
        if (menuOpenId === id) { setMenuOpenId(null); return; }
        const btn = buttonRefs.current[id];
        if (btn) {
            const rect = btn.getBoundingClientRect();
            setMenuPosition({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
        }
        setMenuOpenId(id);
    };

    const handleSort = (field) => {
        if (sortField === field) setSortOrder(o => (o === 'asc' ? 'desc' : 'asc'));
        else { setSortField(field); setSortOrder('asc'); }
        setPage(1);
    };

    const copyName = async (row) => {
        if (!(await copyText(row.name))) return;
        setCopiedId(row.id);
        clearTimeout(copyTimer.current);
        copyTimer.current = setTimeout(() => setCopiedId(null), 1200);
    };

    const confirmDelete = async () => {
        const row = pendingDelete;
        setPendingDelete(null);
        try {
            await deleteLibraryImage(row.id);
            if (rows.length === 1 && page > 1) setPage(p => p - 1);
            setReloadKey(k => k + 1);
            if (onNotify) onNotify('success', 'delete');
        } catch (err) {
            if (onNotify) onNotify('error', 'delete');
        }
    };

    const columns = [
        {
            label: 'Image',
            field: 'image',
            sortable: false,
            width: '80px',
            render: (r) => (
                <img
                    src={r.url || noImage}
                    alt=""
                    onError={e => { e.target.onerror = null; e.target.src = noImage; }}
                    style={{ width: 32, height: 32, objectFit: 'contain', mixBlendMode: 'darken', display: 'block' }}
                />
            ),
        },
        {
            label: 'Image Name',
            field: 'name',
            sortable: true,
            width: '340px',
            render: (r) => (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, maxWidth: '100%' }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
                    <button
                        type="button"
                        title="Copy"
                        onClick={(e) => { e.stopPropagation(); copyName(r); }}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: 0, border: 'none', background: 'none', cursor: 'pointer', color: '#6C757D', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}
                    >
                        <Copy size={15} />{copiedId === r.id && 'Copied!'}
                    </button>
                </span>
            ),
        },
        { label: 'Used In', field: 'used', sortable: false, width: '200px', render: usedInLabel },
        { label: 'Last Modified Time', field: 'modified_at', sortable: true, width: '200px', render: (r) => formatDateTime(r.modifiedAt) },
    ];

    const menuRow = rows.find(r => r.id === menuOpenId);
    const isLibraryEmpty = loaded && count === 0 && !searchTerm;

    return (
        <div className="tab-content" id="trade-in-listing-image-content">
            <div className="info-section">
                <div className="info-card" style={{ overflow: 'unset' }}>
                    <div className="entities-tab">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                            <div className="search-box" style={{ width: 300, position: 'relative', border: '1px solid var(--Gray-600, #6C757D)', background: '#FFF' }}>
                                <Search size={20} color="#6C757D" />
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { setSearchTerm(searchInput.trim()); setPage(1); } }}
                                />
                                {searchInput && (
                                    <button
                                        type="button"
                                        onClick={() => { setSearchInput(''); setSearchTerm(''); setPage(1); }}
                                        style={{ position: 'absolute', right: 4, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 4 }}
                                    >
                                        <X size={16} />
                                    </button>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => setImportOpen(true)}
                                style={{
                                    height: 34, minWidth: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '0 10px',
                                    border: 'none', borderRadius: 8, background: '#212529', color: '#FFFFFF', boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                                    fontFamily: 'Montserrat', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                }}
                            >
                                <CloudUpload size={18} /> Import
                            </button>
                        </div>

                        <div className="table-wrapper" style={{ border: 'none' }}>
                            <DynamicTable
                                columns={columns}
                                data={rows}
                                sortField={sortField}
                                sortOrder={sortOrder}
                                handleSort={handleSort}
                                onActionClick={(id, e) => toggleMenu(id, e)}
                                buttonRefs={buttonRefs}
                                rowKey="id"
                                dropdownOpenId={menuOpenId}
                                searchTerm={searchTerm}
                                emptyImage={isLibraryEmpty ? noBrandImage : emptySearchImg}
                                emptyTitle={isLibraryEmpty ? 'No Image Found' : 'No Results Found'}
                                emptyMessage={isLibraryEmpty ? 'Nothing here yet. Import images to get started!' : "No results match what you're looking for"}
                                showActions={true}
                                showCheckbox={false}
                                suppressHighlight={true}
                            />

                            {menuRow && (
                                <div
                                    ref={menuRef}
                                    className="backdrop-cls"
                                    style={{ position: 'fixed', top: menuPosition.top, right: menuPosition.right, background: 'white', boxShadow: '0px 1px 4px 0px #0000001A', zIndex: 1000, minWidth: 150, borderRadius: 8 }}
                                    onClick={e => e.stopPropagation()}
                                >
                                    <div className="btn-align-forall">
                                        <div className="common-for-all">
                                            <button
                                                // deactivate-cls forces red with !important, so a
                                                // disabled Delete goes without it to show grey.
                                                className={isLocked(menuRow) ? undefined : 'deactivate-cls'}
                                                disabled={isLocked(menuRow)}
                                                title={isLocked(menuRow) ? (menuRow.isSystem ? 'Default images cannot be deleted' : 'Linked to a listing — remove it from every Listing Item first') : undefined}
                                                onClick={() => { setMenuOpenId(null); setPendingDelete(menuRow); }}
                                                style={isLocked(menuRow) ? { color: '#6C757D', cursor: 'not-allowed' } : undefined}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'end', padding: '8px 0' }}>
                            {count > 0 && (
                                <span style={{ fontSize: 12, fontWeight: 400, color: '#212529', fontFamily: 'Montserrat, sans-serif' }}>
                                    {(page - 1) * PAGE_SIZE + 1} - {(page - 1) * PAGE_SIZE + rows.length} of {count}
                                </span>
                            )}
                            <Pagination currentPage={page} totalPages={Math.ceil(count / PAGE_SIZE) || 1} onPageChange={setPage} />
                        </div>
                    </div>
                </div>
            </div>

            {importOpen && (
                <TradeInImportImagesModal
                    onClose={() => setImportOpen(false)}
                    onImported={() => {
                        // Newest first, so the imported images are what the user sees.
                        setSortField('modified_at');
                        setSortOrder('desc');
                        setPage(1);
                        setReloadKey(k => k + 1);
                    }}
                />
            )}

            <DeleteConfirmationModal
                isOpen={Boolean(pendingDelete)}
                onCancel={() => setPendingDelete(null)}
                onConfirm={confirmDelete}
                entityName="Listing Image"
            />
        </div>
    );
}
